import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

import { listNotifications } from "@/api/notifications";
import type { NotificationOut } from "@/api/schemas";
import { useAuth } from "@/stores/auth";
import { useNotifications } from "@/stores/notifications";
import { useWebSocket } from "@/ws/use-websocket";

const TOAST_LABELS: Record<string, string> = {
  ticket_assigned: "Ticket assigned to you",
  ticket_mentioned: "You were mentioned",
  ticket_reply_received: "New reply on your ticket",
  ticket_sla_warning: "SLA warning",
  ticket_sla_breached: "SLA breached",
};

const TOAST_BURST_MS = 300;

/**
 * Mounted next to ``<QueryInvalidator>`` in ``__root``. Three jobs:
 *
 * 1. Fetches the latest notifications + unread count when the user signs in,
 *    seeding the Zustand store so the bell badge is correct on first paint.
 * 2. Subscribes to the ``notification`` WS event and patches the store live.
 *    Burst-debounces toasts so a tick of the SLA cron emitting 5 events
 *    surfaces a single toast instead of stacking five.
 * 3. Re-fetches whenever the auth state flips back to authenticated (e.g.
 *    after logout/login from the same tab).
 *
 * Returns null — purely a side-effect component.
 */
export function NotificationsBootstrap() {
  const isAuthed = useAuth((s) => s.status === "authenticated");
  const queryClient = useQueryClient();
  const hydrate = useNotifications((s) => s.hydrate);
  const prepend = useNotifications((s) => s.prepend);
  const burstRef = useRef<{ count: number; timer: number | null }>({ count: 0, timer: null });

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => listNotifications({ limit: 50 }),
    enabled: isAuthed,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data) {
      hydrate(data.items as NotificationOut[], data.unread_count);
    }
  }, [data, hydrate]);

  const onIncoming = useCallback(
    (raw: unknown) => {
      if (!isAuthed) return;
      const evt = raw as
        | { id?: string; event_type?: string; payload?: Record<string, unknown>; created_at?: string }
        | null;
      if (!evt?.id || !evt.event_type) return;

      const notification: NotificationOut = {
        id: evt.id,
        event_type: evt.event_type,
        channel: "websocket",
        payload: evt.payload ?? {},
        status: "sent",
        error_message: null,
        sent_at: evt.created_at ?? null,
        read_at: null,
        created_at: evt.created_at ?? new Date().toISOString(),
      } as NotificationOut;
      prepend(notification);
      // Trigger a refetch of the bell list so persisted ordering matches the
      // server's UUIDv7-sortable ids on next dropdown open.
      queryClient.invalidateQueries({ queryKey: ["notifications"] });

      const label = TOAST_LABELS[evt.event_type] ?? "Notification";
      const burst = burstRef.current;
      burst.count += 1;
      if (burst.timer !== null) {
        return;
      }
      burst.timer = window.setTimeout(() => {
        const total = burst.count;
        burst.count = 0;
        burst.timer = null;
        toast(
          total === 1 ? label : `${label} (${total} new)`,
          { duration: 4000 },
        );
      }, TOAST_BURST_MS);
    },
    [isAuthed, prepend, queryClient],
  );

  useWebSocket("notification", onIncoming);

  return null;
}
